/**
 * Sales user's incentive surface — comprehensive view (Chunk E3).
 *
 * Top KPIs (4 cards):
 *   • Pending payouts          — legacy IncentivePayout, status=pending
 *   • Approved payouts         — legacy IncentivePayout, status=approved
 *   • Paid payouts             — legacy IncentivePayout, status=paid
 *   • Deal-closure earnings    — sum of "my slice" across locked+paid closures
 *
 * Sections:
 *   1. Deal closures           — list/group toggle, status filter, client name
 *                                column, "my share" per row, links to detail.
 *   2. Spiffs & one-off payouts — legacy table unchanged.
 *
 * Group-by-client view rolls up multiple closures for the same client into
 * one expandable row showing the per-client subtotal.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Trophy, ChevronRight, Sparkles, ChevronDown, ChevronUp, Filter, List, FolderTree,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../store/auth';
import { StatusChip } from '../admin/IncentiveDealClosures.jsx';

const STATUS_CHIP = {
    pending:   'bg-amber-100 text-amber-700',
    approved:  'bg-blue-100 text-blue-700',
    paid:      'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-rose-100 text-rose-700',
};

const STATUS_FILTERS = [
    { key: 'all',     label: 'All' },
    { key: 'pending', label: 'In progress' },     // anything not locked or paid
    { key: 'locked',  label: 'Locked' },
    { key: 'paid',    label: 'Paid' },
];

export default function SalesIncentives() {
    const me = useAuth(s => s.user);
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState([]);
    const [closures, setClosures] = useState([]);
    const [view, setView]       = useState('list');   // 'list' | 'grouped'
    const [statusF, setStatusF] = useState('all');

    useEffect(() => {
        api.get('/incentives/payouts/me').then(r => { setItems(r.data.items); setTotals(r.data.totals); });
        api.get('/client-incentives').then(r => setClosures(r.data.rows || []));
    }, []);

    /* ── Roll-up totals ── */
    const totalsByStatus = Object.fromEntries(totals.map(t => [t._id, t]));
    const sumLegacy = (s) => totalsByStatus[s]?.amount || 0;

    /* Personal-slice rollup across deal closures (locked + paid only — we don't
       count in-progress / pending as "earned" yet). */
    const dealClosureEarnings = useMemo(() => {
        return closures.reduce((sum, r) => {
            if (!['locked', 'paid'].includes(r.status)) return sum;
            const slice = myShare(r, me?._id);
            return sum + (slice?.amount || 0);
        }, 0);
    }, [closures, me?._id]);

    /* ── Filtered closures (drives both list + grouped) ── */
    const filteredClosures = useMemo(() => {
        if (statusF === 'all') return closures;
        if (statusF === 'pending') return closures.filter(r => !['locked', 'paid'].includes(r.status));
        return closures.filter(r => r.status === statusF);
    }, [closures, statusF]);

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-extrabold">Incentives</h1>
                <p className="text-sm text-ink-500">Your deal-closure earnings plus any one-off spiffs.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card label="Deal closures" value={dealClosureEarnings} accent="bg-brand-teal-50 text-brand-teal-700" />
                <Card label="Pending"  value={sumLegacy('pending')}  accent="bg-amber-50 text-amber-700" />
                <Card label="Approved" value={sumLegacy('approved')} accent="bg-blue-50 text-blue-700" />
                <Card label="Paid"     value={sumLegacy('paid')}     accent="bg-emerald-50 text-emerald-700" />
            </div>

            {/* ── Deal-closure section ── */}
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-brand-teal-700" />
                        <h2 className="font-bold">Deal closures</h2>
                        <span className="text-[11px] text-ink-500">{filteredClosures.length} of {closures.length}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Filter className="h-3.5 w-3.5 text-ink-500" />
                        {STATUS_FILTERS.map(f => (
                            <button
                                key={f.key}
                                className={`text-xs px-2 py-1 rounded-full transition
                                    ${statusF === f.key ? 'bg-brand-teal text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
                                onClick={() => setStatusF(f.key)}
                            >{f.label}</button>
                        ))}
                        <span className="text-ink-300 mx-1">·</span>
                        <button
                            className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${view === 'list' ? 'bg-ink-200 text-ink-900' : 'text-ink-500 hover:bg-ink-100'}`}
                            onClick={() => setView('list')}
                            title="Flat list"
                        ><List className="h-3.5 w-3.5" /> List</button>
                        <button
                            className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${view === 'grouped' ? 'bg-ink-200 text-ink-900' : 'text-ink-500 hover:bg-ink-100'}`}
                            onClick={() => setView('grouped')}
                            title="Group by client"
                        ><FolderTree className="h-3.5 w-3.5" /> By client</button>
                    </div>
                </div>

                {filteredClosures.length === 0 ? (
                    <div className="ag-card p-6 text-center text-sm text-ink-500">
                        Nothing matches that filter. Once a client of yours is fully paid and admin confirms it,
                        your share appears here.
                    </div>
                ) : view === 'grouped'
                    ? <GroupedClosures rows={filteredClosures} meId={me?._id} />
                    : <FlatClosures rows={filteredClosures} meId={me?._id} />
                }
            </div>

            {/* ── Legacy spiffs ── */}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-600" />
                    <h2 className="font-bold">Spiffs &amp; one-off payouts</h2>
                </div>
                <div className="ag-card p-0 overflow-x-auto">
                    <table className="ag-table">
                        <thead><tr><th>Month</th><th>Rule / note</th><th>Amount</th><th>Status</th><th>Approved</th></tr></thead>
                        <tbody>
                            {items.map(p => (
                                <tr key={p._id}>
                                    <td className="font-mono text-xs">{p.monthKey}</td>
                                    <td>{p.rule?.name || p.note || '—'}</td>
                                    <td>₹ {p.amountINR.toLocaleString('en-IN')}</td>
                                    <td><span className={`ag-chip ${STATUS_CHIP[p.status]}`}>{p.status}</span></td>
                                    <td className="text-xs text-ink-500">{p.approvedAt ? new Date(p.approvedAt).toLocaleDateString() : '—'}</td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={5} className="text-center text-ink-500 py-8">No payouts yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* ─────────── Flat closure list ─────────── */
function FlatClosures({ rows, meId }) {
    return (
        <div className="ag-card p-0 overflow-x-auto">
            <table className="ag-table">
                <thead><tr>
                    <th>Status</th><th>Client</th><th>Deal</th><th>Pool</th><th>Your share</th><th>Created</th><th></th>
                </tr></thead>
                <tbody>
                    {rows.map(r => {
                        const mine = myShare(r, meId);
                        return (
                            <tr key={r._id}>
                                <td><StatusChip status={r.status} /></td>
                                <td className="font-medium">{r.clientName || '—'}</td>
                                <td className="tabular-nums">₹ {(r.dealAmount || 0).toLocaleString('en-IN')}</td>
                                <td className="tabular-nums">₹ {(r.poolAmount || 0).toLocaleString('en-IN')} <span className="text-[11px] text-ink-500">({r.poolPercent}%)</span></td>
                                <td className="tabular-nums">
                                    {mine
                                        ? <>₹ {mine.amount.toLocaleString('en-IN')} <span className="text-[11px] text-ink-500">({mine.percent.toFixed(2)}%{mine.role ? ` · ${mine.role.toUpperCase()}` : ''})</span></>
                                        : '—'}
                                </td>
                                <td className="text-xs text-ink-500">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                                <td className="text-right">
                                    <Link className="ag-btn-ghost text-xs" to={`/sales/incentives/closures/${r._id}`}>
                                        Open <ChevronRight className="h-3.5 w-3.5" />
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ─────────── Grouped-by-client closure list ─────────── */
function GroupedClosures({ rows, meId }) {
    const groups = useMemo(() => {
        const g = new Map();
        for (const r of rows) {
            const key = r.clientName || '—';
            if (!g.has(key)) g.set(key, []);
            g.get(key).push(r);
        }
        /* Sort groups by total descending so biggest clients land at top. */
        return Array.from(g.entries())
            .map(([clientName, list]) => {
                const myTotal = list.reduce((s, r) => s + (myShare(r, meId)?.amount || 0), 0);
                return { clientName, list, myTotal };
            })
            .sort((a, b) => b.myTotal - a.myTotal);
    }, [rows, meId]);

    const [open, setOpen] = useState(() => new Set(groups.slice(0, 3).map(g => g.clientName)));

    return (
        <div className="space-y-2">
            {groups.map(g => {
                const isOpen = open.has(g.clientName);
                return (
                    <div key={g.clientName} className="ag-card overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setOpen(o => {
                                const n = new Set(o);
                                if (n.has(g.clientName)) n.delete(g.clientName); else n.add(g.clientName);
                                return n;
                            })}
                            className="w-full flex items-center gap-3 px-5 py-3 hover:bg-ink-50 transition text-left"
                        >
                            {isOpen
                                ? <ChevronUp className="h-4 w-4 text-ink-500 shrink-0" />
                                : <ChevronDown className="h-4 w-4 text-ink-500 shrink-0" />}
                            <span className="font-bold flex-1 min-w-0 truncate">{g.clientName}</span>
                            <span className="text-[11px] text-ink-500 shrink-0">
                                {g.list.length} deal{g.list.length === 1 ? '' : 's'}
                            </span>
                            <span className="font-semibold text-brand-teal-700 tabular-nums shrink-0">
                                ₹ {g.myTotal.toLocaleString('en-IN')}
                            </span>
                        </button>
                        {isOpen && (
                            <div className="border-t border-ink-100 overflow-x-auto">
                                <table className="ag-table">
                                    <thead><tr>
                                        <th>Status</th><th>Deal</th><th>Your share</th><th>Created</th><th></th>
                                    </tr></thead>
                                    <tbody>
                                        {g.list.map(r => {
                                            const mine = myShare(r, meId);
                                            return (
                                                <tr key={r._id}>
                                                    <td><StatusChip status={r.status} /></td>
                                                    <td className="tabular-nums">₹ {(r.dealAmount || 0).toLocaleString('en-IN')}</td>
                                                    <td className="tabular-nums">
                                                        {mine ? `₹ ${mine.amount.toLocaleString('en-IN')} (${mine.percent.toFixed(2)}%)` : '—'}
                                                    </td>
                                                    <td className="text-xs text-ink-500">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                                                    <td className="text-right">
                                                        <Link className="ag-btn-ghost text-xs" to={`/sales/incentives/closures/${r._id}`}>
                                                            Open <ChevronRight className="h-3.5 w-3.5" />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function Card({ label, value, accent }) {
    return (
        <div className="ag-card p-5 flex items-start justify-between">
            <div>
                <div className="text-xs uppercase tracking-wide text-ink-500 font-semibold">{label}</div>
                <div className="text-2xl font-extrabold mt-1">₹ {Number(value || 0).toLocaleString('en-IN')}</div>
            </div>
            <div className={`p-2.5 rounded-xl ${accent}`}><Trophy className="h-5 w-5" /></div>
        </div>
    );
}

function myShare(r, meId) {
    if (!meId) return null;
    const match = (a) => String(a.user?._id || a.user) === String(meId);
    const hit = (r.salesAllocations || []).find(match) ||
                (r.opsAllocations   || []).find(match);
    return hit ? { amount: hit.amount || 0, percent: hit.percent || 0, role: hit.role } : null;
}
